// Hook to connect audio system with game events
import { useEffect } from 'react'
import { EventBus, GameEvents } from '@/game/utils/EventBus'
import { playSound, SOUND_IDS } from '@/systems/audioSystem'
import { useAudioStore } from '@/store/audioStore'

export function useGameAudio() {
  const { sfxEnabled, initializeAudio, playMusic } = useAudioStore()
  
  // Initialize audio on first user interaction. Browsers block audio until a
  // gesture, so this is also where the soundtrack starts after a mid-game
  // reload (GAME_STARTED only fires from the menu, not on reload).
  useEffect(() => {
    const handleInteraction = () => {
      initializeAudio()
      const audio = useAudioStore.getState()
      if (audio.musicEnabled && !audio.isMusicPlaying) {
        audio.playMusic()
      }
      document.removeEventListener('click', handleInteraction)
      document.removeEventListener('keydown', handleInteraction)
    }

    document.addEventListener('click', handleInteraction)
    document.addEventListener('keydown', handleInteraction)

    return () => {
      document.removeEventListener('click', handleInteraction)
      document.removeEventListener('keydown', handleInteraction)
    }
  }, [initializeAudio])
  
  // Connect sounds to game events.
  // IMPORTANT: only deliberate, discrete moments make a sound. Passive
  // simulation ticks (DAY_CHANGED every in-game day, TRANSACTION on every
  // income/expense) used to chime constantly — that was the random "noise"
  // the player heard while time ran. Those are intentionally gone.
  useEffect(() => {
    if (!sfxEnabled) return

    // NOTE: all SFX are globally muted for now (SFX_ENABLED in audioSystem.ts),
    // pending a modern sound pass. These handlers stay wired so playSound() does
    // nothing today and re-enabling sound is a single one-line flip.
    const handlers: Record<string, () => void> = {
      [GameEvents.CONSTRUCTION_COMPLETED]: () => playSound(SOUND_IDS.BUILD),
      [GameEvents.ACHIEVEMENT_UNLOCKED]: () => playSound(SOUND_IDS.ACHIEVEMENT),
      [GameEvents.EVENT_RESOLVED]: () => playSound(SOUND_IDS.SUCCESS),
      [GameEvents.TOURNAMENT_WON]: () => playSound(SOUND_IDS.SUCCESS),
    }

    Object.entries(handlers).forEach(([event, handler]) => {
      EventBus.on(event, handler)
    })

    return () => {
      Object.entries(handlers).forEach(([event, handler]) => {
        EventBus.off(event, handler)
      })
    }
  }, [sfxEnabled])
  
  // Start the selected library track when a game begins
  useEffect(() => {
    const handleGameStarted = () => {
      playMusic()
    }

    EventBus.on(GameEvents.GAME_STARTED, handleGameStarted)

    return () => {
      EventBus.off(GameEvents.GAME_STARTED, handleGameStarted)
    }
  }, [playMusic])
}
